import { z } from 'zod';
import {
  categoryFilterSchema,
  contentInputSchema,
  contentStateSchema,
  productContentChangeSchema,
  productStatusSchema,
  publishBlockerSchema,
  shopperHiddenReasonSchema,
  specListSchema,
  type ContentState,
} from './catalog.js';

/**
 * The content engine (spec §06, milestone ד; docs/AI_CONTENT.md): every store gets its own
 * marketing copy of the catalog products, written by an AI provider from the product's facts and
 * Avrahami's text. Facts are never rewritten. Enum values are shared with the database enums; a
 * test checks that they match exactly.
 */

/**
 * What a job writes.
 * - PRODUCT_COPY: the marketing copy of one product
 * - STORE_SEO: the store's own search texts (the title and the description of its home page,
 *   "קידום בגוגל" in the store's settings); the job names no product
 * - PRODUCT_FLYER: the texts of one product's marketing flyer. No longer queued: a flyer is drawn
 *   from its store's template and product page (adr/0053); the kind stays for the old rows
 * - STORE_HERO: the words of the store's opening banner on its home page (adr/0034); the job
 *   names no product
 */
export const AI_JOB_KINDS = ['PRODUCT_COPY', 'STORE_SEO', 'PRODUCT_FLYER', 'STORE_HERO'] as const;
export const aiJobKindSchema = z.enum(AI_JOB_KINDS);
export type AiJobKind = z.infer<typeof aiJobKindSchema>;

/**
 * - QUEUED: waiting for the worker, or for its next attempt after a failed one (`runAfter`)
 * - RUNNING: the worker is on it
 * - SUCCEEDED: the copy was written
 * - FAILED: out of attempts (the dead letter). The product keeps its facts only and stays
 *   hidden; the platform staff can retry it
 * - CANCELLED: no longer needed (the product was removed, or the copy was written by hand)
 */
export const AI_JOB_STATUSES = ['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELLED'] as const;
export const aiJobStatusSchema = z.enum(AI_JOB_STATUSES);
export type AiJobStatus = z.infer<typeof aiJobStatusSchema>;

/**
 * Why a job exists.
 * - STORE_SETUP: the first copy of a new store's catalog (provisioning)
 * - PRODUCT_PULL: a product the store pulled later, or one that still had no copy
 * - OWNER_REQUEST: another version of a product's copy was asked for by hand (by the platform
 *   staff, who manage the copy since adr/0023; the name is from when the store's owner did)
 * - SOURCE_UPDATE: the catalog's marketing text of the product changed, so the copy the engine
 *   wrote from it is written again (the daily catalog update, adr/0026); for copy edited by hand
 *   only the product's strengths are written again (adr/0048)
 * - FEATURES_MISSING: the product has copy but no strengths (copy written before adr/0048):
 *   only the strengths (and the lists of adr/0054) are written, the copy stays as it is
 * - LISTS_MISSING: the product has copy and strengths but not the "ideal for" and care lists
 *   (copy written before adr/0054): only those lists are written
 * - LISTS_RESTORE: the store gave a list it had edited by hand back to the engine (adr/0055):
 *   the lists the store has not edited are written again, the copy stays as it is
 */
export const AI_JOB_TRIGGERS = [
  'STORE_SETUP',
  'PRODUCT_PULL',
  'OWNER_REQUEST',
  'SOURCE_UPDATE',
  'FEATURES_MISSING',
  'LISTS_MISSING',
  'LISTS_RESTORE',
] as const;
export const aiJobTriggerSchema = z.enum(AI_JOB_TRIGGERS);
export type AiJobTrigger = z.infer<typeof aiJobTriggerSchema>;

/**
 * Why the last attempt of a job failed, stored as a code (never the provider's message or the
 * generated text).
 * - FACT_GUARD: the text has a number or a model code that is not in the product's facts
 * - LANGUAGE: the text has letters of a foreign script that the correction pass did not fix
 * - TOO_SIMILAR: the text repeats Avrahami's text instead of rewriting it
 * - INVALID_OUTPUT: the provider answered with something unusable (empty, cut off, refused)
 * - PROVIDER_UNAVAILABLE: the provider could not be reached, or asked us to slow down
 * - STALLED: the worker kept dying on this job
 * - UNEXPECTED: anything else; the worker log has the details under the job id
 */
export const AI_JOB_ERROR_CODES = [
  'FACT_GUARD',
  'LANGUAGE',
  'TOO_SIMILAR',
  'INVALID_OUTPUT',
  'PROVIDER_UNAVAILABLE',
  'STALLED',
  'UNEXPECTED',
] as const;
export const aiJobErrorCodeSchema = z.enum(AI_JOB_ERROR_CODES);
export type AiJobErrorCode = z.infer<typeof aiJobErrorCodeSchema>;

/** What a provider call was for. */
export const AI_USAGE_PURPOSES = ['WRITE', 'LANGUAGE_FIX'] as const;
export const aiUsagePurposeSchema = z.enum(AI_USAGE_PURPOSES);
export type AiUsagePurpose = z.infer<typeof aiUsagePurposeSchema>;

/** Money of the provider's bill: millionths of a US dollar. */
export const costMicrosSchema = z.number().int().min(0);
export const MICROS_PER_USD = 1_000_000;

/** The worker job: write the waiting copy of one tenant. */
export const aiContentJobSchema = z.object({ tenantId: z.uuid() });
export type AiContentJob = z.infer<typeof aiContentJobSchema>;

/** Content states that count as "the store has its own copy". */
export const OWN_COPY_STATES: readonly ContentState[] = [
  'AI_GENERATED',
  'APPROVED',
  'TENANT_EDITED',
];

// ---------------------------------------------------------------------------------------------
// Platform surface: one store's copy, managed by the platform staff (`platform.ai.manage`,
// adr/0023). The store's own admin has no copy screen; its owner edits a product's text by hand
// on the product page.
// ---------------------------------------------------------------------------------------------

const count = z.number().int().min(0);

export const aiCopySummarySchema = z.object({
  /** Generated copy is approved before it is published (default: published at once). */
  requiresApproval: z.boolean(),
  /** Catalog products of the store that were not removed, by the state of their copy. */
  products: z.object({
    total: count,
    /** No copy of the store's own yet: hidden from shoppers. */
    withoutCopy: count,
    /** Of those, the ones nothing is being written for and nothing failed (WITHOUT_COPY). */
    unqueued: count,
    /** Written by the engine and not yet approved or edited. */
    generated: count,
    approved: count,
    edited: count,
  }),
  jobs: z.object({ queued: count, running: count, failed: count }),
  /** Copy is waiting because this month's writing quota is used up. */
  paused: z.boolean(),
  /** The platform staff stopped the writing for this store; waiting copy stays in the queue. */
  stopped: z.boolean(),
  /** The store's own search texts (its settings' "קידום בגוגל"), which the engine writes too. */
  storeSeo: z.object({
    title: z.string().nullable(),
    description: z.string().nullable(),
    /** The latest job that writes them; null when there never was one. */
    job: z
      .object({ status: aiJobStatusSchema, errorCode: aiJobErrorCodeSchema.nullable() })
      .nullable(),
  }),
});
export type AiCopySummary = z.infer<typeof aiCopySummarySchema>;

/**
 * - GENERATED: written by the engine, not yet approved or edited
 * - IN_PROGRESS: a job is queued or running
 * - FAILED: the last job failed; the product has no copy
 * - WITHOUT_COPY: no copy and no job
 * - READY: approved, or edited by hand
 */
export const AI_COPY_FILTERS = [
  'GENERATED',
  'IN_PROGRESS',
  'FAILED',
  'WITHOUT_COPY',
  'READY',
] as const;
export const aiCopyFilterSchema = z.enum(AI_COPY_FILTERS);
export type AiCopyFilter = z.infer<typeof aiCopyFilterSchema>;

export const aiCopyItemSchema = z.object({
  productId: z.uuid(),
  /** The product's version: the optimistic lock of an edit of its copy. */
  version: z.number().int(),
  name: z.string(),
  sku: z.string(),
  status: productStatusSchema,
  contentState: contentStateSchema,
  title: z.string().nullable(),
  description: z.string().nullable(),
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  /** The first image's file name under the store's media route; null without one. */
  image: z.string().nullable(),
  /** The product's latest job; null when it never had one. */
  job: z
    .object({
      status: aiJobStatusSchema,
      errorCode: aiJobErrorCodeSchema.nullable(),
      attempts: count,
      updatedAt: z.iso.datetime(),
    })
    .nullable(),
  /** Why shoppers do not see the product even when it is ACTIVE; null when nothing hides it. */
  hiddenFromShoppers: shopperHiddenReasonSchema.nullable(),
  /** Empty when the product may be shown to shoppers. */
  publishBlockers: z.array(publishBlockerSchema),
});
export type AiCopyItem = z.infer<typeof aiCopyItemSchema>;

export const aiCopyListQuerySchema = z.object({
  filter: aiCopyFilterSchema.default('GENERATED'),
  /** Part of the name, title, SKU or model. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Products placed directly in this category of the store. */
  categoryId: categoryFilterSchema.optional(),
});
export type AiCopyListQuery = z.infer<typeof aiCopyListQuerySchema>;

/** The state the store's categories are counted under (`productCategoryGroups`). */
export const aiCopyGroupsQuerySchema = aiCopyListQuerySchema.pick({ filter: true });
export type AiCopyGroupsQuery = z.infer<typeof aiCopyGroupsQuerySchema>;

/** Products one request may name. */
export const MAX_AI_COPY_SELECTION = 200;
/** Products one "write again" request may name: every one is a paid provider call. */
export const MAX_AI_COPY_REGENERATION = 25;

const productIds = (max: number) =>
  z
    .array(z.uuid())
    .min(1)
    .max(max)
    .refine((ids) => new Set(ids).size === ids.length, 'duplicate product');

/** Approve generated copy: the ticked products, or everything that waits for approval. */
export const approveAiCopyRequestSchema = z.discriminatedUnion('selection', [
  z.object({ selection: z.literal('ALL') }),
  z.object({ selection: z.literal('SELECTED'), productIds: productIds(MAX_AI_COPY_SELECTION) }),
]);
export type ApproveAiCopyRequest = z.infer<typeof approveAiCopyRequestSchema>;

export const approveAiCopyResponseSchema = z.object({
  approved: count,
  /** Of those, drafts that became visible to shoppers. */
  published: count,
});
export type ApproveAiCopyResponse = z.infer<typeof approveAiCopyResponseSchema>;

/**
 * Ask the engine for another version: of the named products' copy, or of all the copy it wrote
 * that nobody approved or edited since (`ALL_GENERATED`; every product is a paid provider call).
 */
export const regenerateAiCopyRequestSchema = z.discriminatedUnion('selection', [
  z.object({ selection: z.literal('ALL_GENERATED') }),
  z.object({
    selection: z.literal('SELECTED'),
    productIds: productIds(MAX_AI_COPY_REGENERATION),
  }),
]);
export type RegenerateAiCopyRequest = z.infer<typeof regenerateAiCopyRequestSchema>;

/** How many jobs a request queued (duplicates of waiting work are not queued again). */
export const aiCopyQueuedResponseSchema = z.object({ queued: count });
export type AiCopyQueuedResponse = z.infer<typeof aiCopyQueuedResponseSchema>;

/**
 * The switches of one store's copy: approve generated copy before it is published, and stop or
 * continue the writing. A request names the ones it changes.
 */
export const updateAiCopySettingsRequestSchema = z
  .object({ requiresApproval: z.boolean().optional(), stopped: z.boolean().optional() })
  .refine((value) => value.requiresApproval !== undefined || value.stopped !== undefined, {
    message: 'nothing to change',
  });
export type UpdateAiCopySettingsRequest = z.infer<typeof updateAiCopySettingsRequestSchema>;

export const aiCopyProductParamsSchema = z.object({ tenantId: z.uuid(), productId: z.uuid() });

/** The platform staff edit one product's copy by hand, under the product's optimistic lock. */
export const updateAiCopyRequestSchema = z.object({
  version: z.number().int().min(1),
  content: contentInputSchema,
});
export type UpdateAiCopyRequest = z.infer<typeof updateAiCopyRequestSchema>;

/** The product's version after the edit. */
export const updateAiCopyResponseSchema = z.object({ version: z.number().int() });
export type UpdateAiCopyResponse = z.infer<typeof updateAiCopyResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Editing the copy of a whole group (adr/0043): the products of a category (with every category
// below it), of a manufacturer, or of both. The text that is there is edited; nothing is written
// again by the engine.
// ---------------------------------------------------------------------------------------------

export const COPY_FIELD_NAMES = ['title', 'description', 'seoTitle', 'seoDescription'] as const;
export const copyFieldSchema = z.enum(COPY_FIELD_NAMES);
export type CopyFieldName = z.infer<typeof copyFieldSchema>;

/** How long each field of the copy may be (the same limits as `contentInputSchema`). */
export const COPY_FIELD_LIMITS: Record<CopyFieldName, number> = {
  title: 200,
  description: 20_000,
  seoTitle: 70,
  seoDescription: 320,
};

/**
 * The products a group edit reaches: the catalog products of the store that have copy and were
 * not removed, in a category (or below it; `none`: in no category), of a manufacturer (exactly as
 * the catalog names it), or both. At least one of the two is named.
 */
export const aiCopyGroupSchema = z
  .object({
    categoryId: categoryFilterSchema.optional(),
    manufacturer: z.string().trim().min(1).max(100).optional(),
  })
  .refine((group) => group.categoryId !== undefined || group.manufacturer !== undefined, {
    message: 'name a category or a manufacturer',
  });
export type AiCopyGroup = z.infer<typeof aiCopyGroupSchema>;

/**
 * - REPLACE: every occurrence of `find` (exactly, letter case included) becomes `replace`, which
 *   may be empty to remove it
 * - PREPEND / APPEND: `text` goes before or after the field's text (a space between them, an
 *   empty line in the description)
 */
export const GROUP_EDIT_ACTIONS = ['REPLACE', 'PREPEND', 'APPEND'] as const;
export const groupEditActionSchema = z.enum(GROUP_EDIT_ACTIONS);
export type GroupEditAction = z.infer<typeof groupEditActionSchema>;

const groupText = z.string().trim().min(1).max(2_000);

export const groupEditSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('REPLACE'),
    find: z
      .string()
      .min(1)
      .max(500)
      .refine((text) => text.trim() !== '', 'empty'),
    replace: z.string().max(2_000),
  }),
  z.object({ action: z.literal('PREPEND'), text: groupText }),
  z.object({ action: z.literal('APPEND'), text: groupText }),
]);
export type GroupEdit = z.infer<typeof groupEditSchema>;

/** One edit of one field of the copy of a whole group; `preview` only counts and shows examples. */
export const aiCopyGroupEditRequestSchema = z.object({
  group: aiCopyGroupSchema,
  field: copyFieldSchema,
  edit: groupEditSchema,
  preview: z.boolean().default(false),
});
export type AiCopyGroupEditRequest = z.input<typeof aiCopyGroupEditRequestSchema>;

/** Products a preview shows as examples of the change. */
export const GROUP_EDIT_EXAMPLES = 3;

export const aiCopyGroupEditResponseSchema = z.object({
  /** Products of the group that have copy. */
  inGroup: count,
  /** Products whose text changes (or, on a preview, would change). */
  changed: count,
  /** Products left as they were: the text would be longer than the field allows, or empty. */
  skipped: count,
  /** Of the changed products, drafts that became visible to shoppers (never on a preview). */
  published: count,
  examples: z
    .array(
      z.object({
        productId: z.uuid(),
        name: z.string(),
        before: z.string(),
        after: z.string(),
      }),
    )
    .max(GROUP_EDIT_EXAMPLES),
});
export type AiCopyGroupEditResponse = z.infer<typeof aiCopyGroupEditResponseSchema>;

/** The manufacturers of the store's catalog products with copy, for choosing a group. */
export const aiCopyManufacturersSchema = z.object({
  items: z.array(z.object({ name: z.string(), productCount: count })),
});
export type AiCopyManufacturers = z.infer<typeof aiCopyManufacturersSchema>;

// ---------------------------------------------------------------------------------------------
// One catalog product's page as the platform staff manage it (adr/0043): the same product page
// the store's admin has, with the copy edited by hand and never written again from here.
// ---------------------------------------------------------------------------------------------

/**
 * A change of the product: its copy, its status (never "removed": the platform removes a product
 * from a store by excluding it), and the store's own specification rows.
 */
export const platformProductUpdateRequestSchema = z.object({
  version: z.number().int().min(1),
  content: productContentChangeSchema.optional(),
  status: z.enum(['DRAFT', 'ACTIVE', 'HIDDEN']).optional(),
  facts: z.object({ extraSpecs: specListSchema }).strict().optional(),
});
export type PlatformProductUpdateRequest = z.infer<typeof platformProductUpdateRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Platform surface: cost and volume control (`platform.ai.view`, `platform.ai.manage`)
// ---------------------------------------------------------------------------------------------

export const aiJobCountsSchema = z.object({
  queued: count,
  running: count,
  succeeded: count,
  failed: count,
});
export type AiJobCounts = z.infer<typeof aiJobCountsSchema>;

export const platformAiTenantSchema = z.object({
  tenantId: z.uuid(),
  slug: z.string(),
  displayName: z.string(),
  /** Spent since the start of the month. */
  spentMicros: costMicrosSchema,
  /** The store's monthly limit: its own, or the platform default. */
  limitMicros: costMicrosSchema,
  limitIsDefault: z.boolean(),
  calls: count,
  inputTokens: count,
  outputTokens: count,
  jobs: aiJobCountsSchema,
});
export type PlatformAiTenant = z.infer<typeof platformAiTenantSchema>;

export const platformAiOverviewSchema = z.object({
  /** The start of the month the amounts are counted from (UTC). */
  periodStart: z.iso.datetime(),
  spentMicros: costMicrosSchema,
  /** The platform's monthly limit for all stores together. */
  limitMicros: costMicrosSchema,
  defaultTenantLimitMicros: costMicrosSchema,
  /** Stores with usage this month, open jobs or a limit of their own; highest spend first. */
  tenants: z.array(platformAiTenantSchema),
});
export type PlatformAiOverview = z.infer<typeof platformAiOverviewSchema>;

/** One store's content state, for its card in the platform admin. */
export const tenantAiStatusSchema = z.object({
  products: z.object({ total: count, withCopy: count }),
  jobs: aiJobCountsSchema,
  spentMicros: costMicrosSchema,
  limitMicros: costMicrosSchema,
  limitIsDefault: z.boolean(),
});
export type TenantAiStatus = z.infer<typeof tenantAiStatusSchema>;

/** Up to 100,000 dollars a month; `null` returns the store to the platform default. */
export const setTenantAiBudgetRequestSchema = z.object({
  monthlyLimitMicros: costMicrosSchema.max(100_000 * MICROS_PER_USD).nullable(),
});
export type SetTenantAiBudgetRequest = z.infer<typeof setTenantAiBudgetRequestSchema>;
